import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../components/AuthContext';
import { useToast } from '../../../components/ToastContext';
import { useOrg } from '../../../components/OrgContext';
import { logAudit } from '../../../hooks/useAudit';
import PageBar from '../../../components/PageBar';
import PageLoading from '../../../components/PageLoading';

// Settings -> Sites. Global Admin only.
//
// Every site there is, which is not the same question as my_orgs() answers.
// my_orgs() means "where may I work", and a switched-off site is not somewhere
// you work - so it is hidden there, and would be hidden here too if this
// screen used it. all_sites() is the other question, and it is the one that
// lets a switched-off site be switched back on.
//
// What this screen does not do is edit another site's settings. Those are
// edited from inside the site they belong to: pick a site here, switch into
// it, and Site Settings is about that site. Every read and write in the app
// then stays on one side of the isolation line, which is the property that
// makes a second company safe to add at all.

function slugify(s) {
  return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export default function Sites() {
  const { profile } = useAuth();
  const toast = useToast();
  const { switchTo, switching, reload: reloadOrgs } = useOrg();

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(null);   // null = still asking
  const [creating, setCreating] = useState(false);
  const [draft, setDraft]     = useState({ name: '', slug: '' });
  const [busy, setBusy]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [global, sites] = await Promise.all([
      supabase.rpc('is_global_admin'),
      supabase.rpc('all_sites'),
    ]);
    setAllowed(!!global.data);
    setRows(sites.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function create() {
    const name = draft.name.trim();
    if (!name) { toast.error('A site needs a name.'); return; }
    setBusy(true);
    const { error } = await supabase.rpc('create_site', {
      site_name: name,
      site_slug: draft.slug.trim() || null,
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    await logAudit(profile, 'SITE_CREATED', 'site', { target_name: name });
    toast.success(name + ' created. Switch into it to set it up.');
    setDraft({ name: '', slug: '' });
    setCreating(false);
    await reloadOrgs();
    load();
  }

  async function setActive(site, next) {
    const verb = next ? 'Switch on' : 'Switch off';
    if (!window.confirm(
      verb + ' ' + site.name + '?' + (next ? '' : ' Its people will not be able to reach it.')
    )) return;
    const { error } = await supabase.from('organizations').update({ is_active: next }).eq('id', site.id);
    if (error) { toast.error(error.message); return; }
    await logAudit(profile, next ? 'SITE_ACTIVATED' : 'SITE_DEACTIVATED', 'site', { target_name: site.name });
    toast.success(site.name + (next ? ' is on.' : ' is off.'));
    await reloadOrgs();
    load();
  }

  if (allowed === false) {
    return (
      <>
        <PageBar title="Sites" />
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
          Only a Global Admin can see every site. Your own site's settings are under Site Settings.
        </div>
      </>
    );
  }

  return (
    <>
      <PageBar
        title="Sites"
        meta={loading ? undefined : rows.length + (rows.length === 1 ? ' site' : ' sites')}
        actions={<button className="tb-btn tb-btn-primary" onClick={() => setCreating(c => !c)}>+ New Site</button>}
      />

      {creating && (
        <div className="card" style={{ padding: '20px 24px', marginBottom: 16 }}>
          <div className="ua-section-head" style={{ marginBottom: 14 }}>
            <span className="ua-section-title">New site</span>
            <span className="ua-section-note">
              A new site starts empty, with nobody in it. You can already reach every site, so
              switch into it and add its people from Users &amp; Access - that screen knows how
              to do it properly.
            </span>
          </div>
          <div className="ua-fields">
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className="fld-label">Site name</span>
              <input className="fld" value={draft.name} autoFocus
                onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className="fld-label">Web address</span>
              <input className="fld" placeholder={slugify(draft.name) || 'made-from-the-name'}
                value={draft.slug}
                onChange={e => setDraft(d => ({ ...d, slug: e.target.value }))} />
              <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                Leave it blank and it is made from the name. It is not changed afterwards.
              </span>
            </label>
          </div>
          <div className="ua-actions">
            <button className="tb-btn tb-btn-ghost" onClick={() => { setCreating(false); setDraft({ name: '', slug: '' }); }}>
              Cancel
            </button>
            <span style={{ flex: 1 }} />
            <button className="tb-btn tb-btn-primary" disabled={busy} onClick={create}>
              {busy ? 'Creating...' : 'Create site'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="card"><PageLoading rows={3} /></div>
      ) : (
        <div className="ss-sites">
          {rows.map(s => (
            <div key={s.id} className={'ss-site' + (s.is_current ? ' here' : '') + (s.is_active ? '' : ' off')}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="ua-ga-title">
                  {s.name}
                  {s.is_current && <span className="ua-here" style={{ marginLeft: 9 }}>you are here</span>}
                  {!s.is_active && <span className="ua-inactive" style={{ marginLeft: 9 }}>Switched off</span>}
                </div>
                <div className="ua-ga-note">
                  /{s.slug} - {s.member_count} {s.member_count === 1 ? 'person' : 'people'}
                </div>
              </div>
              <div className="ss-site-actions">
                {s.is_active && !s.is_current && (
                  <button className="tb-btn" disabled={switching} onClick={() => switchTo(s.id)}>
                    {switching ? 'Switching...' : 'Switch into it'}
                  </button>
                )}
                {s.is_current && <span className="ua-rolepill">Settings are under Site Settings</span>}
                {/* Switching off the site you are standing in leaves the header
                    with a site it cannot find, and takes away the screen that
                    would switch it back on. The database refuses it too. */}
                {!(s.is_current && s.is_active) && (
                  <button className="tb-btn tb-btn-ghost" onClick={() => setActive(s, !s.is_active)}>
                    {s.is_active ? 'Switch off' : 'Switch on'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="ss-note" style={{ marginTop: 16 }}>
        A site's own settings - its name, address, modules and the rest - are edited from
        inside it. Switch into a site and its Site Settings page is about that site.
      </div>
    </>
  );
}
